import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { useInsightsContext } from '../context/InsightsContext'
import {
  DatasetColumn,
  DatasetRow,
  FilterValues,
} from '../types/insights.types'
import { cursorFromLink } from '../utils/cursorFromLink'
import { serialiseFiltersForApi } from '../utils/filterSerialiser'

export type DatasetSortDirection = 'ASC' | 'DESC'

export type DatasetSort = {
  /** Column key to sort by. */
  column: string
  direction: DatasetSortDirection
}

export type UseInsightsDatasetOptions = {
  /** Active top-level filter values (the domain's pinned filters are merged on top). */
  filters?: FilterValues
  sort?: DatasetSort
  pageSize?: number
  /** Column projection (`?columns=`) — request only the columns the table renders. */
  columns?: string[]
  /** Adds the columns of these column groups (`?columnGroups=`). */
  columnGroups?: string[]
}

export type UseInsightsDatasetResult = {
  /** Self-describing column metadata from the response (label, dataType, sortable, values). */
  columns: DatasetColumn[]
  rows: DatasetRow[]
  totalResults: number
  isLoading: boolean
  error: boolean
  /** Cursor pagination — `null` when there is no further page in that direction. */
  nextPage: (() => void) | null
  previousPage: (() => void) | null
}

const DATASET_STALE_TIME_MS = 10_000
export const DEFAULT_PAGE_SIZE = 20

/**
 * Fetches one paginated page of a dataset's rows — applying the active filters
 * (+ the domain's pinned filters), the current sort, and keyset cursor pagination
 * (via the response `_links`). Surfaces the self-describing column metadata so the
 * table can render headers/cells from the response. Changing the filters, sort or
 * page size resets to the first page.
 */
export const useInsightsDataset = (
  dataset: string,
  {
    filters = {},
    sort,
    pageSize = DEFAULT_PAGE_SIZE,
    columns,
    columnGroups,
  }: UseInsightsDatasetOptions = {}
): UseInsightsDatasetResult => {
  const { domain, service } = useInsightsContext()
  const [cursor, setCursor] = useState<string | undefined>(undefined)

  // The request params excluding the cursor; a change here is a new query (new
  // filters/sort/page size) that resets pagination to the first page. The caller
  // passes `useFilters().values`, which already folds in the domain's lockedFilters.
  const baseParams = useMemo<Record<string, string | string[] | number>>(() => {
    const base = serialiseFiltersForApi(filters)

    return {
      ...base,
      pageSize,
      ...(sort ? { sort: sort.column, direction: sort.direction } : {}),
      ...(columns && columns.length > 0 ? { columns: columns.join(',') } : {}),
      ...(columnGroups && columnGroups.length > 0
        ? { columnGroups: columnGroups.join(',') }
        : {}),
    }
  }, [filters, sort, pageSize, columns, columnGroups])

  // Reset the cursor whenever the base query changes (derive-state-on-render).
  const baseKey = JSON.stringify(baseParams)
  const [prevBaseKey, setPrevBaseKey] = useState(baseKey)
  if (prevBaseKey !== baseKey) {
    setPrevBaseKey(baseKey)
    setCursor(undefined)
  }

  const params = useMemo(
    () => (cursor ? { ...baseParams, cursor } : baseParams),
    [baseParams, cursor]
  )

  const { data, isLoading, isError } = useQuery({
    queryKey: [`insights/${domain}/datasets/${dataset}`, params],
    queryFn: () => service.getDataset({ domain, dataset, params }),
    placeholderData: keepPreviousData,
    staleTime: DATASET_STALE_TIME_MS,
    retry: 1,
  })

  const envelope = data?.data
  const nextCursor = cursorFromLink(envelope?._links.next)
  const prevCursor = cursorFromLink(envelope?._links.prev)

  return {
    columns: envelope?.columns ?? [],
    rows: envelope?.data ?? [],
    totalResults: envelope?.totalResults ?? 0,
    isLoading,
    error: isError,
    nextPage: nextCursor ? () => setCursor(nextCursor) : null,
    previousPage: prevCursor ? () => setCursor(prevCursor) : null,
  }
}
